// src/components/block/liveStreamManagement/ProductionControlRoom_Deck.tsx

import React, { useState, useCallback, useRef } from 'react';
import { Video, Settings, Image, Music, Eye, ExternalLink, Volume2, VolumeX, Film, Clapperboard, RefreshCw } from 'lucide-react';

import {
  VideoSource, ImageSlot, SoundSlot, VideoOverlaySlot, JingleSlot, LayoutSlot,
  SLOT_COUNT, SourceAudioConfig,
} from '../../../contexts/MediaStreamContext';
import { DeckKeyMap } from '../../../dataTypes/liveStreamManagment/types_deck';
import { formatKeyBinding } from '../../../dataTypes/liveStreamManagment/utils_deck';

import { SourceAliasMap, resolveSourceLabel } from './SourceAliasConfigModal';
import PupitreConfigModal from './PupitreConfigModal';
import SourceConfigModal, { AudioEffectState, AudioEffectId, StereoVolume, DEFAULT_AUDIO_EFFECTS, DEFAULT_STEREO_VOLUME } from './SourceConfigModal';

import { HlsProbeStatus } from './useHlsProber';

import { Window } from '../../widget/windows/window';

// ── Palette ──────────────────────────────────────────────────────────────────

interface ColorState { led: string; text: string; bg: string; border: string; shadow: string; }
const EMPTY: ColorState = { led: '#334155', text: '#ffffff', bg: '#1e293b', border: '#334155', shadow: 'none' };
const PALETTES = {
  video:   { avail: { led:'#4ade80', text:'#fff', bg:'linear-gradient(135deg,#14532d,#166534)', border:'#4ade80', shadow:'0 0 8px #4ade8050' },  active:{ led:'#ff4444', text:'#fff', bg:'linear-gradient(135deg,#166534,#22c55e)', border:'#ff4444', shadow:'0 0 12px #86efac80' } },
  preview: { avail: { led:'#38bdf8', text:'#fff', bg:'linear-gradient(135deg,#0c4a6e,#075985)', border:'#38bdf8', shadow:'0 0 8px #38bdf850' },  active:{ led:'#22c55e', text:'#fff', bg:'linear-gradient(135deg,#075985,#0284c7)', border:'#22c55e', shadow:'0 0 12px #7dd3fc80' } },
  image:   { avail: { led:'#22d3ee', text:'#fff', bg:'linear-gradient(135deg,#164e63,#155e75)', border:'#22d3ee', shadow:'0 0 8px #22d3ee50' },  active:{ led:'#a5f3fc', text:'#fff', bg:'linear-gradient(135deg,#155e75,#06b6d4)', border:'#a5f3fc', shadow:'0 0 12px #a5f3fc80' } },
  sound:   { avail: { led:'#facc15', text:'#fff', bg:'linear-gradient(135deg,#713f12,#854d0e)', border:'#facc15', shadow:'0 0 8px #facc1550' },  active:{ led:'#fef08a', text:'#fff', bg:'linear-gradient(135deg,#854d0e,#ca8a04)', border:'#fef08a', shadow:'0 0 12px #fef08a80' } },
  pip:     { avail: { led:'#e879f9', text:'#fff', bg:'linear-gradient(135deg,#581c87,#6b21a8)', border:'#e879f9', shadow:'0 0 8px #e879f950' },  active:{ led:'#f0abfc', text:'#fff', bg:'linear-gradient(135deg,#6b21a8,#a855f7)', border:'#f0abfc', shadow:'0 0 12px #f0abfc80' } },
  jingle:  { avail: { led:'#f87171', text:'#fff', bg:'linear-gradient(135deg,#7f1d1d,#991b1b)', border:'#f87171', shadow:'0 0 8px #f8717150' },  active:{ led:'#fca5a5', text:'#fff', bg:'linear-gradient(135deg,#991b1b,#ef4444)', border:'#fca5a5', shadow:'0 0 12px #fca5a580' } },
  layout:  { avail: { led:'#60a5fa', text:'#fff', bg:'linear-gradient(135deg,#1e3a5f,#1e40af)', border:'#60a5fa', shadow:'0 0 8px #60a5fa50' },  active:{ led:'#93c5fd', text:'#fff', bg:'linear-gradient(135deg,#1e40af,#3b82f6)', border:'#93c5fd', shadow:'0 0 12px #93c5fd80' } },
} as const;
type PaletteKey = keyof typeof PALETTES;
const stateOfRow = (row: PaletteKey, has: boolean, active: boolean): ColorState =>
  !has ? EMPTY : active ? PALETTES[row].active : PALETTES[row].avail;
const EMOJI_RE = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\ufe0f\s]+/u;
const cleanLabel = (s: string | null | undefined) => (s ?? '').replace(EMOJI_RE, '').trim();

// ── Utilitaire boxShadow ──────────────────────────────────────────────────────
// 'none' est un mot-clé CSS qui ne peut pas être combiné avec d'autres ombres.
// Si on écrit "none, inset 0 2px 4px …" le navigateur invalide toute la
// propriété et conserve la valeur précédente → le halo persiste.
const buildBoxShadow = (shadow: string, extra = 'inset 0 2px 4px rgba(0,0,0,0.4)'): string =>
  shadow === 'none' ? extra : `${shadow}, ${extra}`;

const GRID_14: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(12, 1fr) 12px 1fr', gap: 6, alignItems: 'end' };
const GRID_14_STRETCH: React.CSSProperties = { ...GRID_14, alignItems: 'stretch' };

const RowLabel: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:8 }}>
    {icon && <span style={{ display:'flex', alignItems:'center', opacity:0.7 }}>{icon}</span>}
    <span style={{ fontSize:12, fontWeight:600, color:'#ffffff' }}>{children}</span>
  </div>
);

// ── Icône Power ───────────────────────────────────────────────────────────────
const PowerIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M7 1.5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4.5 3.2A5 5 0 1 0 9.5 3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// ── DeckButton ────────────────────────────────────────────────────────────────
interface DBProps { label: string | null; state: ColorState; isActive?: boolean; keyBinding?: string | null; title?: string; disabled?: boolean; onClick: () => void; extra?: React.ReactNode; }
const DeckButton: React.FC<DBProps> = ({ label, state, isActive = false, keyBinding, title, disabled, onClick, extra }) => (
  <button onClick={onClick} disabled={disabled} title={title} style={{
    aspectRatio: '1', width: '100%', background: state.bg,
    border: `${isActive ? 6 : 2}px solid ${state.border}`,
    borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', padding: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 4, transition: 'all 0.15s',
    // CORRECTION : buildBoxShadow évite "none, inset …" qui est du CSS invalide
    boxShadow: buildBoxShadow(state.shadow),
    minWidth: 0,
  }}>
    <div style={{ width:7, height:7, borderRadius:'50%', background:state.led, boxShadow: state === EMPTY ? 'none' : `0 0 6px ${state.led}` }} />
    <span style={{ fontSize:12, fontWeight:700, color:'#fff', textAlign:'center', lineHeight:1.25, padding:'0 3px',
      overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' as React.CSSProperties['WebkitBoxOrient'],
      wordBreak:'break-word', opacity: disabled ? 0.5 : 1 }}>{cleanLabel(label) || '—'}</span>
    {keyBinding && <span style={{ fontSize:10, fontFamily:'monospace', color:'#ffffff', lineHeight:1 }}>[{keyBinding}]</span>}
    {extra}
  </button>
);

const PROBING_STATE: ColorState = { led: '#94a3b8', text: '#fff', bg: 'linear-gradient(135deg,#1e293b,#334155)', border: '#64748b', shadow: '0 0 4px #64748b30' };
const FAILED_STATE: ColorState  = { led: '#f97316', text: '#fff', bg: 'linear-gradient(135deg,#1c1917,#292524)', border: '#f97316', shadow: '0 0 6px #f9731640' };

const HLS_BLINK_STYLE = `
  @keyframes hls-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
  @keyframes probe-dots { 0%, 20% { content: '.'; } 40%, 60% { content: '..'; } 80%, 100% { content: '...'; } }
  @keyframes probe-fade { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
`;

// ── VideoDeckButton ───────────────────────────────────────────────────────────
interface VDBProps {
  label: string | null; state: ColorState; isActive?: boolean; keyBinding?: string | null;
  title?: string; disabled?: boolean; onClick: () => void; sourceId: string;
  audioLevel: number; audioCfg: SourceAudioConfig;
  onVolumeChange: (volume: number) => void; onMuteToggle: () => void;
  onOpenConfig?: () => void; onRetry?: () => void;
  isPoweredOff?: boolean; onTogglePower?: () => void;
}

const VideoDeckButton: React.FC<VDBProps> = ({
  label, state, keyBinding, title, disabled, onClick, isActive = false,
  audioLevel, audioCfg, onVolumeChange, onMuteToggle, onOpenConfig,
  onRetry, isPoweredOff, onTogglePower,
}) => {
  const levelColor = audioLevel > 80 ? '#ef4444' : audioLevel > 60 ? '#f59e0b' : '#22c55e';
  const hasAudio = !disabled;

  // CORRECTION : powerBtn est null si onTogglePower n'est pas fourni (slot vide)
  const powerBtn = onTogglePower ? (
    <button
      onClick={e => { e.stopPropagation(); onTogglePower(); }}
      title={isPoweredOff ? 'Réactiver la source' : 'Désactiver la source'}
      style={{
        background:'none', border:'none', cursor:'pointer', padding:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        color: isPoweredOff ? '#ef4444' : 'rgba(255,255,255,0.35)',
        width:20, height:20, transition:'color 0.15s',
      }}
      onMouseEnter={e => (e.currentTarget.style.color = isPoweredOff ? '#fca5a5' : 'rgba(255,255,255,0.7)')}
      onMouseLeave={e => (e.currentTarget.style.color = isPoweredOff ? '#ef4444' : 'rgba(255,255,255,0.35)')}
    ><PowerIcon size={14} /></button>
  ) : null;

  const configBtn = (
    <button
      onClick={e => { e.stopPropagation(); onOpenConfig && onOpenConfig(); }}
      title="Configurer"
      style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.35)', width:20, height:20, transition:'color 0.15s' }}
      onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}
      onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
    ><Settings style={{ width:14, height:14 }} /></button>
  );

  return (
    <div style={{
      background: state.bg,
      border: `${isActive ? 6 : 2}px solid ${state.border}`,
      borderRadius: 8, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      transition: 'all 0.15s',
      // CORRECTION : buildBoxShadow évite "none, inset …" invalide en CSS
      boxShadow: buildBoxShadow(state.shadow),
      minWidth: 0, width: '100%',
    }}>
      <style>{HLS_BLINK_STYLE}</style>

      <button onClick={onClick} disabled={disabled} title={title} style={{ background:'none', border:'none', padding:'12px 2px 4px', cursor:disabled?'not-allowed':'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4, minWidth:0, flex:'0 0 auto' }}>
        <div style={{ width:7, height:7, borderRadius:'50%', background:state.led, animation:state===PROBING_STATE?'hls-blink 1s ease-in-out infinite':'none', boxShadow:state===EMPTY?'none':`0 0 6px ${state.led}` }} />
        <span style={{ fontSize:12, fontWeight:700, color:'#fff', textAlign:'center', lineHeight:1.25, padding:'0 2px', overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' as React.CSSProperties['WebkitBoxOrient'], wordBreak:'break-word', opacity:disabled?0.5:1 }}>
          {cleanLabel(label) || '—'}
        </span>
        {state === PROBING_STATE && (
          <span style={{ fontSize: 8, color: '#94a3b8', lineHeight: 1, display: 'flex', gap: 2, justifyContent: 'center' }}>
            {[0, 1, 2].map(i => (
              <span key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: '#94a3b8', display: 'inline-block', animation: `probe-fade 1.2s ease-in-out ${i * 0.3}s infinite` }} />
            ))}
          </span>
        )}
        {keyBinding && <span style={{ fontSize:10, fontFamily:'monospace', color:'#fff', lineHeight:1 }}>[{keyBinding}]</span>}
      </button>

      {onRetry && !isPoweredOff && (
        <div style={{ display:'flex', justifyContent:'center', paddingBottom:4 }}>
          <button onClick={e => { e.stopPropagation(); onRetry(); }} title="Réessayer"
            style={{ background:'rgba(249,115,22,0.15)', border:'1px solid rgba(249,115,22,0.5)', borderRadius:4, cursor:'pointer', padding:'2px 6px', display:'flex', alignItems:'center', gap:3, color:'#f97316' }}
            onMouseEnter={e=>(e.currentTarget.style.background='rgba(249,115,22,0.3)')}
            onMouseLeave={e=>(e.currentTarget.style.background='rgba(249,115,22,0.15)')}>
            <RefreshCw style={{ width:10, height:10 }} />
            <span style={{ fontSize:8, fontWeight:700 }}>Réessayer</span>
          </button>
        </div>
      )}

      {hasAudio ? (
        <div style={{ padding:'4px 5px 2px', marginTop:'auto', display:'flex', flexDirection:'column', gap:3 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
            {['L','R'].map(ch => (
              <div key={ch} style={{ display:'flex', alignItems:'center', gap:2 }}>
                <span style={{ fontSize:7, color:'#fff', lineHeight:1, width:7, textAlign:'right' }}>{ch}</span>
                <div style={{ flex:1, height:4, background:'rgba(255,255,255,0.08)', borderRadius:1.5, overflow:'hidden' }}>
                  <div style={{ height:'100%', borderRadius:1.5, width:`${audioLevel}%`, background:levelColor, transition:'width 30ms' }} />
                </div>
              </div>
            ))}
          </div>
          <input type="range" min={0} max={1} step={0.05} value={audioCfg.muted ? 0 : audioCfg.volume}
            onChange={e => { e.stopPropagation(); onVolumeChange(+e.target.value); }}
            onClick={e => e.stopPropagation()}
            title={`Volume : ${Math.round(audioCfg.volume * 100)}%`}
            style={{ width:'100%', height:10, cursor:'pointer', accentColor:audioCfg.muted?'#475569':'#4ade80', margin:0, display:'block' }} />
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
            <button onClick={e => { e.stopPropagation(); onMuteToggle(); }} title={audioCfg.muted?'Unmute':'Mute'}
              style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', justifyContent:'center', color:audioCfg.muted?'#ef4444':'rgba(255,255,255,0.45)', width:20, height:20 }}>
              {audioCfg.muted ? <VolumeX style={{ width:15, height:15 }} /> : <Volume2 style={{ width:15, height:15 }} />}
            </button>
            {powerBtn}
            {configBtn}
          </div>
        </div>
      ) : (
        <div style={{ padding:'3px 5px 5px', marginTop:'auto', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
          {powerBtn}
          {configBtn}
        </div>
      )}
    </div>
  );
};

// ── T-Bar Fader ───────────────────────────────────────────────────────────────
interface TBarFaderProps { value: number; onChange: (v: number) => void; disabled?: boolean; }
const TBarFader: React.FC<TBarFaderProps> = ({ value, onChange, disabled }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const TRACK_H  = 240;
  const HANDLE_H = 16;
  const OVERFLOW = 20;

  const tipY      = (1 - value) * TRACK_H;
  const handleTop = tipY - HANDLE_H / 2;

  const PCT_0DB = 0.16;

  const pctPos       = tipY / TRACK_H;
  const handleFill   = pctPos < PCT_0DB          ? '#dc2626'
                     : pctPos < PCT_0DB + 0.09   ? '#f59e0b'
                     :                             '#22c55e';
  const handleStroke = pctPos < PCT_0DB          ? '#fca5a5'
                     : pctPos < PCT_0DB + 0.09   ? '#fde68a'
                     :                             '#86efac';

  const startDrag = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    dragging.current = true;

    const update = (clientY: number) => {
      const rect = trackRef.current && trackRef.current.getBoundingClientRect();
      if (!rect) return;
      const y = Math.max(0, Math.min(TRACK_H, clientY - rect.top));
      onChange(1 - y / TRACK_H);
    };

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if (!dragging.current) return;
      update('touches' in ev ? ev.touches[0].clientY : ev.clientY);
    };
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove as EventListener);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove as EventListener, { passive: false });
    window.addEventListener('touchend', onUp);
    update('touches' in e
      ? (e as React.TouchEvent).touches[0].clientY
      : (e as React.MouseEvent).clientY);
  }, [disabled, onChange]);

  const marks = [
    { label: '+6', pct: 0.00,    major: true  },
    { label: '0',  pct: PCT_0DB, major: true  },
    { label: '-6', pct: 0.36,    major: false },
    { label: '-12',pct: 0.56,    major: false },
    { label: '-∞', pct: 1.00,    major: false },
  ];

  const dbLabel = (() => {
    if (pctPos <= 0.01)                          return '+6 dB';
    if (Math.abs(pctPos - PCT_0DB) < 0.015)     return '0 dB';
    if (Math.abs(pctPos - 0.36)    < 0.015)     return '-6 dB';
    if (Math.abs(pctPos - 0.56)    < 0.015)     return '-12 dB';
    if (pctPos >= 0.99)                          return '-∞';
    return `${Math.round(value * 100)} %`;
  })();

  const trackW = 28;
  const svgW   = trackW + OVERFLOW * 2;

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, width:'100%', userSelect:'none' }}>
      <div
        ref={trackRef}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        style={{
          position: 'relative',
          width: trackW,
          height: TRACK_H,
          background: `linear-gradient(to bottom,
            rgba(239,68,68,0.70)   0%,
            rgba(239,68,68,0.70)   ${PCT_0DB * 100}%,
            rgba(245,158,11,0.75)  ${PCT_0DB * 100}%,
            rgba(245,158,11,0.75)  ${(PCT_0DB + 0.09) * 100}%,
            rgba(34,197,94,0.50)   ${(PCT_0DB + 0.09) * 100}%,
            rgba(22,101,52,0.35)   100%
          )`,
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 6,
          cursor: disabled ? 'not-allowed' : 'ns-resize',
          overflow: 'visible',
        }}
      >
        {marks.map(({ label, pct, major }) => (
          <React.Fragment key={label}>
            <div style={{
              position: 'absolute',
              top: pct * TRACK_H - 0.5,
              left: major ? 1 : 4,
              right: major ? 1 : 4,
              height: 1,
              background: major ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.22)',
              pointerEvents: 'none',
            }} />
            <span style={{
              position: 'absolute',
              top: pct * TRACK_H,
              left: trackW + OVERFLOW + 3,
              transform: 'translateY(-50%)',
              fontSize: 7,
              fontFamily: 'monospace',
              color: major ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.28)',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}>{label}</span>
          </React.Fragment>
        ))}
        <svg
          style={{
            position: 'absolute',
            left: -OVERFLOW,
            top: handleTop,
            width: svgW,
            height: HANDLE_H,
            overflow: 'visible',
            pointerEvents: 'none',
            cursor: disabled ? 'not-allowed' : 'grab',
          }}
        >
          <polygon
            points={`${svgW / 2},0 ${svgW},${HANDLE_H / 2} ${svgW / 2},${HANDLE_H} 0,${HANDLE_H / 2}`}
            fill={handleFill}
            stroke={handleStroke}
            strokeWidth={1.5}
            fillOpacity={0.92}
          />
          <line
            x1={svgW * 0.22} y1={HANDLE_H / 2}
            x2={svgW * 0.78} y2={HANDLE_H / 2}
            stroke="rgba(255,255,255,0.40)"
            strokeWidth={1}
          />
        </svg>
      </div>
      <span style={{ fontSize:9, fontFamily:'monospace', color:'rgba(255,255,255,0.45)' }}>
        {dbLabel}
      </span>
    </div>
  );
};

// ── Props ──────────────────────────────────────────────────────────────────────
export interface ProductionControlRoomDeckProps {
  deckSlots: (VideoSource | null)[]; selectedVideoSource: string;
  deckSlotLayouts: (LayoutSlot | null)[];
  onSlotLayoutChange: (slotIndex: number, layout: LayoutSlot | null) => void;
  onDeckPreviewLayout: (layout: LayoutSlot) => void;
  activeLayoutPreviewId: string | null;
  onDeckSwitch: (source: VideoSource, forceNewScreen?: boolean) => void;
  onDeckPreview: (source: VideoSource) => void; deckPreviewSourceId: string | null;
  onDeckCut: () => void; channelUUID: string; serverUrl: string; channelActive: boolean;
  imageSlots: ImageSlot[]; activeImageIds: Set<string>; onToggleImage: (slotId: string) => void;
  soundSlots: SoundSlot[]; activeSoundIds: Set<string>; onTriggerSound: (slot: SoundSlot) => void;
  jingleSlots: JingleSlot[]; activeJingleId: string | null; onTriggerJingle: (slot: JingleSlot) => void;
  videoOverlaySlots: VideoOverlaySlot[]; activeVideoOverlayIds: Set<string>;
  pipVideoEls: React.MutableRefObject<Map<string, { el: HTMLVideoElement; stream: MediaStream }>>;
  onStartVideoOverlay: (slot: VideoOverlaySlot) => void;
  keyMap: DeckKeyMap; onOpenKeyboardShortcuts?: () => void; onKeyMapChange: (km: DeckKeyMap) => void; onOpenMediaboardConfig: () => void;
  onOpenSourceAliasConfig: () => void; sourceAliases: SourceAliasMap; onOpenDeckFullscreen: () => void;
  layoutSlots: LayoutSlot[]; activeLayoutId: string | null; onToggleLayout: (slot: LayoutSlot) => void;
  sourceAudioLevels: Map<string, number>; sourceAudioLevelsLeft?: Map<string, number>; sourceAudioLevelsRight?: Map<string, number>;
  getSourceAudioConfig: (sourceId: string) => SourceAudioConfig;
  onSourceVolumeChange: (sourceId: string, volume: number) => void; onSourceMuteToggle: (sourceId: string) => void;
  availableSources?: VideoSource[];
  onSlotSourceChange: (slotIndex: number, newSource: VideoSource | null) => void;
  onDeckSwitchSource?: (source: VideoSource) => void;
  onAliasChange?: (sourceId: string, alias: string) => void;
  onAliasesChange?: (aliases: SourceAliasMap) => void;
  availableAudioDevices?: { deviceId: string; label: string; kind?: string }[];
  selectedAudioDeviceIds?: Map<string, string>;
  onAudioDeviceChange?: (sourceId: string, audioDeviceId: string) => void;
  stereoVolumes?: Map<string, StereoVolume>; onStereoVolumeChange?: (sourceId: string, stereo: StereoVolume) => void;
  audioEffectsMap?: Map<string, AudioEffectState>; onToggleAudioEffect?: (sourceId: string, effect: AudioEffectId) => void;
  tbarValue?: number; onTbarChange?: (v: number) => void;
  onSourceConfigOpen?:  (sourceId: string, source: VideoSource | null) => Promise<void>;
  onSourceConfigClose?: (sourceId: string | null) => void;
  hlsProbeStatusMap: Map<string, HlsProbeStatus>;
  onRetryHls: (sourceId: string, url: string) => void;
  poweredOffSlots: Set<string>;
  onSlotPowerToggle: (sourceId: string, currentlyOff: boolean) => void;
  // ── Power général régie ───────────────────────────────────────────────────
  isRegiePowered?: boolean;
}

// ── buildVideoRow ─────────────────────────────────────────────────────────────
export interface VideoRow { slot: VideoSource | null; badge: string; isHlsVideo: boolean; isHlsLive: boolean; }

// channelUUID peut être null : voir la copie de DeckV2.
export const buildVideoRow = (deckSlots: (VideoSource | null)[], channelUUID: string | null): VideoRow[] => {
  const rows: VideoRow[] = [];
  for (let i = 0; i < SLOT_COUNT; i++) {
    const slot = i < deckSlots.length ? deckSlots[i] : null;
    const isHlsVideo = !!(channelUUID && slot && slot.type === 'url' && slot.externalUrl && slot.externalUrl.includes(`video_${channelUUID}`));
    const isHlsLive  = !!(channelUUID && slot && slot.type === 'url' && slot.externalUrl && slot.externalUrl.includes(`live_${channelUUID}`));
    rows.push({ slot: slot ? slot : null, badge: String(i + 1), isHlsVideo, isHlsLive });
  }
  return rows;
};

export const buildDefaultVideoRow = (allSources: VideoSource[], channelUUID: string | null): VideoRow[] => {
  const rows: VideoRow[] = [];
  const devices = allSources.filter(s => s.type === 'device');
  for (let i = 0; i < devices.length; i++) {
    rows.push({ slot: devices[i], badge: String(rows.length + 1), isHlsVideo: false, isHlsLive: false });
  }
  const hlsV = allSources.find(s => s.type === 'url' && s.externalUrl && s.externalUrl.includes(`video_${channelUUID}`)) || null;
  rows.push({ slot: hlsV, badge: String(rows.length + 1), isHlsVideo: true, isHlsLive: false });
  while (rows.length < SLOT_COUNT) {
    rows.push({ slot: null, badge: String(rows.length + 1), isHlsVideo: false, isHlsLive: false });
  }
  return rows.slice(0, SLOT_COUNT);
};

// ── PupitreConfigModalWrapper ─────────────────────────────────────────────────
interface PupitreWrapperProps {
  onClose: () => void;
  videoRow: VideoRow[];
  deckSlots: (VideoSource | null)[];
  deckSlotLayouts: (LayoutSlot | null)[];
  availableSources: VideoSource[];
  sourceAliases: SourceAliasMap;
  onAliasesChange: ((aliases: SourceAliasMap) => void) | undefined;
  onSlotSourceChange: (slotIndex: number, newSource: VideoSource | null) => void;
  onSlotLayoutChange: (slotIndex: number, layout: LayoutSlot | null) => void;
  jingleSlots: JingleSlot[];
  layoutSlots: LayoutSlot[];
  imageSlots: ImageSlot[];
  soundSlots: SoundSlot[];
  videoOverlaySlots: VideoOverlaySlot[];
  activeJingleId: string | null;
  activeLayoutId: string | null;
  onTriggerJingle: (slot: JingleSlot) => void;
  onToggleLayout: (slot: LayoutSlot) => void;
  keyMap: DeckKeyMap;
  onKeyMapChange: (km: DeckKeyMap) => void;
  slotLabelFn: (idx: number) => string | null;
}

const PupitreConfigModalWrapper: React.FC<PupitreWrapperProps> = (p) => {
  const vSlots: Array<VideoSource | null> = [];
  const vLayouts: Array<LayoutSlot | null> = [];
  for (let vi = 0; vi < p.videoRow.length; vi++) {
    const row = p.videoRow[vi];
    vSlots.push(row.slot ? row.slot : null);
    if (!row.slot) {
      vLayouts.push(null);
    } else {
      let ri = -1;
      for (let i = 0; i < p.deckSlots.length; i++) {
        if (p.deckSlots[i] && p.deckSlots[i]!.id === row.slot.id) { ri = i; break; }
      }
      vLayouts.push(ri >= 0 ? (p.deckSlotLayouts[ri] ? p.deckSlotLayouts[ri] : null) : null);
    }
  }

  const deckSrcIds: Set<string> = new Set();
  for (let vi = 0; vi < p.videoRow.length; vi++) {
    if (p.videoRow[vi].slot) deckSrcIds.add(p.videoRow[vi].slot!.id);
  }
  const oSources: Array<VideoSource> = [];
  for (let vi = 0; vi < p.videoRow.length; vi++) {
    if (p.videoRow[vi].slot) oSources.push(p.videoRow[vi].slot!);
  }
  for (let i = 0; i < p.availableSources.length; i++) {
    if (!deckSrcIds.has(p.availableSources[i].id)) oSources.push(p.availableSources[i]);
  }

  const usedRaw: Set<number> = new Set();
  const mapping: Array<number> = [];
  for (let vi = 0; vi < p.videoRow.length; vi++) mapping.push(-1);

  for (let vi = 0; vi < p.videoRow.length; vi++) {
    const row = p.videoRow[vi];
    if (row.slot) {
      for (let i = 0; i < p.deckSlots.length; i++) {
        if (p.deckSlots[i] && p.deckSlots[i]!.id === row.slot.id) {
          mapping[vi] = i;
          usedRaw.add(i);
          break;
        }
      }
    }
  }
  const freeRaw: Array<number> = [];
  for (let i = 0; i < p.deckSlots.length; i++) {
    if (!usedRaw.has(i)) freeRaw.push(i);
  }
  let fi = 0;
  for (let vi = 0; vi < p.videoRow.length; vi++) {
    if (!p.videoRow[vi].slot && mapping[vi] === -1 && fi < freeRaw.length) {
      mapping[vi] = freeRaw[fi];
      fi = fi + 1;
    }
  }

  const handleSrcChange = (visualIdx: number, newSource: VideoSource | null) => {
    const rawIdx = mapping[visualIdx];
    if (rawIdx >= 0) p.onSlotSourceChange(rawIdx, newSource);
  };

  const handleLayoutChange = (visualIdx: number, layout: LayoutSlot | null) => {
    const rawIdx = mapping[visualIdx];
    if (rawIdx >= 0) p.onSlotLayoutChange(rawIdx, layout);
  };

  const vLabels: Array<string | null> = [];
  for (let vi = 0; vi < p.videoRow.length; vi++) vLabels.push(p.slotLabelFn(vi));
  const iLabels: Array<string> = [];
  for (let i = 0; i < p.imageSlots.length; i++) iLabels.push(p.imageSlots[i].label ? p.imageSlots[i].label : '');
  const sLabels: Array<string> = [];
  for (let i = 0; i < p.soundSlots.length; i++) sLabels.push(p.soundSlots[i].label ? p.soundSlots[i].label : '');
  const pLabels: Array<string> = [];
  for (let i = 0; i < p.videoOverlaySlots.length; i++) pLabels.push(p.videoOverlaySlots[i].label ? p.videoOverlaySlots[i].label : '');
  const jLabels: Array<string> = [];
  for (let i = 0; i < p.jingleSlots.length; i++) jLabels.push(p.jingleSlots[i].label ? p.jingleSlots[i].label : '');
  const lLabels: Array<string> = [];
  for (let i = 0; i < p.layoutSlots.length; i++) lLabels.push(p.layoutSlots[i].label ? p.layoutSlots[i].label : '');

  return (
    <PupitreConfigModal
      onClose={p.onClose}
      availableSources={oSources}
      deckSlots={vSlots}
      deckSlotLayouts={vLayouts}
      aliases={p.sourceAliases}
      onAliasesChange={function(aliases) { if (p.onAliasesChange) p.onAliasesChange(aliases); }}
      onSlotSourceChange={handleSrcChange}
      onSlotLayoutChange={handleLayoutChange}
      jingleSlots={p.jingleSlots}
      layoutSlots={p.layoutSlots}
      activeJingleId={p.activeJingleId}
      activeLayoutId={p.activeLayoutId}
      onTriggerJingle={p.onTriggerJingle}
      onToggleLayout={p.onToggleLayout}
      keyMap={p.keyMap}
      onKeyMapChange={p.onKeyMapChange}
      videoSlotLabels={vLabels}
      imageSlotLabels={iLabels}
      soundSlotLabels={sLabels}
      pipSlotLabels={pLabels}
      jingleSlotLabels={jLabels}
      layoutSlotLabels={lLabels}
    />
  );
};

// ── Composant principal ───────────────────────────────────────────────────────
const ProductionControlRoom_Deck: React.FC<ProductionControlRoomDeckProps> = (props) => {
  const {
    deckSlots, selectedVideoSource, deckSlotLayouts, onSlotLayoutChange,
    onDeckPreviewLayout, activeLayoutPreviewId,
    onDeckSwitch, onDeckPreview, deckPreviewSourceId, onDeckCut,
    channelUUID, serverUrl, imageSlots, activeImageIds, onToggleImage,
    soundSlots, activeSoundIds, onTriggerSound,
    jingleSlots, activeJingleId, onTriggerJingle,
    videoOverlaySlots, activeVideoOverlayIds, onStartVideoOverlay,
    keyMap, onKeyMapChange, onOpenMediaboardConfig,
    sourceAliases, onOpenDeckFullscreen,
    layoutSlots, activeLayoutId, onToggleLayout,
    sourceAudioLevels, getSourceAudioConfig, onSourceVolumeChange, onSourceMuteToggle,
    onSlotSourceChange, onDeckSwitchSource, onAliasChange, onAliasesChange,
    availableSources, availableAudioDevices, selectedAudioDeviceIds, onAudioDeviceChange,
    stereoVolumes, onStereoVolumeChange, audioEffectsMap, onToggleAudioEffect,
    tbarValue = 1, onTbarChange,
    onSourceConfigOpen, onSourceConfigClose,
    hlsProbeStatusMap, onRetryHls,
    poweredOffSlots, onSlotPowerToggle,
    // AJOUT : power général régie (défaut true = régie active)
    isRegiePowered = true,
  } = props;

  const [cutFlash, setCutFlash] = useState(false);
  const [configSlotIndex, setConfigSlotIndex] = useState<number | null>(null);
  const [pupitreConfigOpen, setPupitreConfigOpen] = useState(false);
  const [pendingPowerOff, setPendingPowerOff] = useState<{ sourceId: string; message: string } | null>(null);

  const handleOpenSourceConfig = useCallback(async (slotIndex: number) => {
    setConfigSlotIndex(slotIndex);
    const layout = deckSlotLayouts[slotIndex];
    const source = deckSlots[slotIndex] ? deckSlots[slotIndex] : null;
    const sourceId = layout ? layout.id : (source ? source.id : `slot-${slotIndex}`);
    if (onSourceConfigOpen) await onSourceConfigOpen(sourceId, source);
  }, [deckSlots, deckSlotLayouts, onSourceConfigOpen]);

  const handleCloseSourceConfig = useCallback(() => {
    if (configSlotIndex !== null) {
      const layout = deckSlotLayouts[configSlotIndex];
      const source = deckSlots[configSlotIndex] ? deckSlots[configSlotIndex] : null;
      const sourceId = layout ? layout.id : (source ? source.id : null);
      if (onSourceConfigClose) onSourceConfigClose(sourceId);
    }
    setConfigSlotIndex(null);
  }, [configSlotIndex, deckSlots, deckSlotLayouts, onSourceConfigClose]);

  const hlsStatus = useCallback((isVideo: boolean): HlsProbeStatus | undefined => {
    const id = isVideo ? `hls-video-${channelUUID}` : `hls-live-${channelUUID}`;
    return hlsProbeStatusMap.get(id);
  }, [hlsProbeStatusMap, channelUUID]);

  const hlsUrl = useCallback((isVideo: boolean): string => {
    const base = serverUrl.replace(/\/$/, '');
    return isVideo ? `${base}/video_${channelUUID}/index.m3u8` : `${base}/live_${channelUUID}/index.m3u8`;
  }, [serverUrl, channelUUID]);

  const handleCut = () => { onDeckCut(); setCutFlash(true); setTimeout(() => setCutFlash(false), 300); };
  const videoRow = buildVideoRow(deckSlots, channelUUID);

  const buildHls = (isV: boolean): VideoSource => {
    const base = serverUrl.replace(/\/$/, '');
    return isV
      ? { id:`hls-video-${channelUUID}`, label:'Playlist', type:'url', externalUrl:`${base}/video_${channelUUID}/index.m3u8` }
      : { id:`hls-live-${channelUUID}`,  label:'OBS, RTMP...',  type:'url', externalUrl:`${base}/live_${channelUUID}/index.m3u8` };
  };

  const isHlsActive = (isV: boolean) => selectedVideoSource === (isV ? `hls-video-${channelUUID}` : `hls-live-${channelUUID}`);

  const srcIdForSlot = (idx: number): string => {
    const layout = deckSlotLayouts[idx];
    if (layout) return layout.id;
    const row = videoRow[idx];
    if (!row) return '';
    return row.slot ? row.slot.id : (row.isHlsVideo ? `hls-video-${channelUUID}` : row.isHlsLive ? `hls-live-${channelUUID}` : '');
  };

  const slotState = (idx: number): ColorState => {
    const srcId = srcIdForSlot(idx);
    if (poweredOffSlots.has(srcId)) return EMPTY;
    const layout = deckSlotLayouts[idx];
    if (layout) {
      const hasContent = layout.background.type !== 'none' || layout.incrustations.length > 0;
      return stateOfRow('video', hasContent, activeLayoutId === layout.id);
    }
    const row = videoRow[idx];
    if (!row) return EMPTY;
    if (row.isHlsVideo || row.isHlsLive) {
      const s = hlsStatus(row.isHlsVideo);
      if (s === 'failed')  return FAILED_STATE;
      if (s === 'probing') return PROBING_STATE;
      if (s === 'ok')      return isHlsActive(row.isHlsVideo) ? PALETTES.video.active : PALETTES.video.avail;
      return EMPTY;
    }
    if (row.slot && row.slot.type === 'url') {
      const s = hlsProbeStatusMap.get(row.slot.id);
      if (s === 'failed')  return FAILED_STATE;
      if (s === 'probing') return PROBING_STATE;
      if (s === 'ok')      return selectedVideoSource === row.slot.id && !activeLayoutId ? PALETTES.video.active : PALETTES.video.avail;
      return EMPTY;
    }
    return stateOfRow('video', !!row.slot, !!(row.slot && row.slot.id === selectedVideoSource && !activeLayoutId));
  };

  const slotLabel = (idx: number): string | null => {
    const layout = deckSlotLayouts[idx];
    if (layout) { const alias = sourceAliases[`layout:${layout.id}`]; return cleanLabel(alias || layout.label); }
    const row = videoRow[idx];
    if (!row) return null;
    if (row.slot) return cleanLabel(resolveSourceLabel(row.slot, sourceAliases));
    if (row.isHlsVideo) return cleanLabel(sourceAliases[`hls-video-${channelUUID}`] || 'Playlist');
    if (row.isHlsLive)  return cleanLabel(sourceAliases[`hls-live-${channelUUID}`]  || 'OBS, RTMP...');
    return null;
  };

  const slotTitle = (idx: number): string => {
    const layout = deckSlotLayouts[idx];
    if (layout) { const hasContent = layout.background.type !== 'none' || layout.incrustations.length > 0; return hasContent ? `[${idx+1}] ${slotLabel(idx)}` : `Scène ${idx+1} — vide`; }
    const row = videoRow[idx];
    if (!row) return `Slot ${idx+1} — vide`;
    const label = slotLabel(idx);
    if (row.isHlsVideo) { const s = hlsStatus(true); if (s==='failed') return 'Playlist — indisponible'; if (s==='probing') return 'Playlist — vérification…'; return s==='ok' ? `[${idx+1}] ${label}` : 'Playlist — indisponible'; }
    if (row.isHlsLive)  { const s = hlsStatus(false); if (s==='failed') return 'OBS, RTMP... — indisponible'; if (s==='probing') return 'OBS, RTMP... — vérification…'; return s==='ok' ? `[${idx+1}] ${label}` : 'OBS, RTMP... — indisponible'; }
    if (row.slot && row.slot.type === 'url') { const s = hlsProbeStatusMap.get(row.slot.id); if (s==='failed') return `${label || row.slot.label} — indisponible`; if (s==='probing') return `${label || row.slot.label} — vérification…`; }
    return row.slot ? `[${idx+1}] ${label}` : `Slot ${idx+1} — vide`;
  };

  const slotDisabled = (idx: number): boolean => {
    const srcId = srcIdForSlot(idx);
    if (poweredOffSlots.has(srcId)) return true;
    const layout = deckSlotLayouts[idx];
    if (layout) return layout.background.type === 'none' && layout.incrustations.length === 0;
    const row = videoRow[idx];
    if (!row) return true;
    if (row.isHlsVideo) { const s = hlsStatus(true);  return s !== 'ok'; }
    if (row.isHlsLive)  { const s = hlsStatus(false); return s !== 'ok'; }
    if (row.slot && row.slot.type === 'url') { const s = hlsProbeStatusMap.get(row.slot.id); return s !== 'ok'; }
    return !row.slot;
  };

  const handleSlotClick = (idx: number) => {
    const layout = deckSlotLayouts[idx];
    if (layout) { onToggleLayout(layout); return; }
    if (activeLayoutId) { const l = layoutSlots.find(l => l.id === activeLayoutId); if (l) onToggleLayout(l); }
    const row = videoRow[idx];
    if (!row) return;
    if (row.slot) { onDeckSwitch(row.slot, row.slot.type === 'screen'); return; }
    if (row.isHlsVideo) { onDeckSwitch(buildHls(true)); return; }
    if (row.isHlsLive)  { onDeckSwitch(buildHls(false)); return; }
  };

  const resolvePreviewSource = (idx: number): VideoSource | null => {
    const row = videoRow[idx];
    if (!row) return null;
    if (row.isHlsVideo) return row.slot ? row.slot : buildHls(true);
    if (row.isHlsLive)  return row.slot ? row.slot : buildHls(false);
    return row.slot ? row.slot : null;
  };

  const previewSlotState = (idx: number): ColorState => {
    const srcId = srcIdForSlot(idx);
    if (poweredOffSlots.has(srcId)) return EMPTY;
    const layout = deckSlotLayouts[idx];
    if (layout) {
      const hasContent = layout.background.type !== 'none' || layout.incrustations.length > 0;
      if (!hasContent) return EMPTY;
      return activeLayoutPreviewId === layout.id ? PALETTES.preview.active : PALETTES.preview.avail;
    }
    const row = videoRow[idx];
    if (!row) return EMPTY;
    if (row.isHlsVideo || row.isHlsLive) { const s = hlsStatus(row.isHlsVideo); if (s !== 'ok') return EMPTY; const src = row.slot ? row.slot : buildHls(row.isHlsVideo); return deckPreviewSourceId === src.id ? PALETTES.preview.active : PALETTES.preview.avail; }
    if (row.slot && row.slot.type === 'url') { const s = hlsProbeStatusMap.get(row.slot.id); if (s !== 'ok') return EMPTY; return deckPreviewSourceId === row.slot.id ? PALETTES.preview.active : PALETTES.preview.avail; }
    const src = resolvePreviewSource(idx); if (!src) return EMPTY;
    return deckPreviewSourceId === src.id ? PALETTES.preview.active : PALETTES.preview.avail;
  };

  const previewSlotLabel = (idx: number): string | null => {
    const layout = deckSlotLayouts[idx];
    if (layout) { const alias = sourceAliases[`layout:${layout.id}`]; return cleanLabel(alias || layout.label); }
    const row = videoRow[idx];
    if (!row) return null;
    if (row.slot) return cleanLabel(resolveSourceLabel(row.slot, sourceAliases));
    if (row.isHlsVideo) return cleanLabel(sourceAliases[`hls-video-${channelUUID}`] || 'Playlist');
    if (row.isHlsLive)  return cleanLabel(sourceAliases[`hls-live-${channelUUID}`]  || 'OBS, RTMP...');
    return null;
  };

  const previewSlotDisabled = (idx: number): boolean => {
    const srcId = srcIdForSlot(idx);
    if (poweredOffSlots.has(srcId)) return true;
    const layout = deckSlotLayouts[idx];
    if (layout) return layout.background.type === 'none' && layout.incrustations.length === 0;
    const row = videoRow[idx];
    if (!row) return true;
    if (row.isHlsVideo) { const s = hlsStatus(true);  return s !== 'ok'; }
    if (row.isHlsLive)  { const s = hlsStatus(false); return s !== 'ok'; }
    if (row.slot && row.slot.type === 'url') { const s = hlsProbeStatusMap.get(row.slot.id); return s !== 'ok'; }
    return !row.slot;
  };

  const handlePreviewClick = (idx: number) => {
    const layout = deckSlotLayouts[idx];
    if (layout) { if (layout.background.type !== 'none' || layout.incrustations.length > 0) onDeckPreviewLayout(layout); return; }
    const src = resolvePreviewSource(idx); if (src) onDeckPreview(src);
  };

  const cutEnabled = !!deckPreviewSourceId || !!activeLayoutPreviewId;
  const cutKeyLabel = keyMap.cut && keyMap.cut[0] ? formatKeyBinding(keyMap.cut[0]) : null;

  const handlePowerOffRequest = useCallback((sourceId: string, isOff: boolean, source: VideoSource | null, row: VideoRow) => {
    if (isOff) {
      onSlotPowerToggle(sourceId, true);
      return;
    }

    const isInFinal   = row.slot && row.slot.id === selectedVideoSource && !activeLayoutId;
    const isInPreview = deckPreviewSourceId === sourceId;
    const sceneInFinal   = activeLayoutId        ? layoutSlots.find(l => l.id === activeLayoutId) && layoutSlots.find(l => l.id === activeLayoutId)!.incrustations.some(i => i.sourceId === sourceId) : false;
    const sceneInPreview = activeLayoutPreviewId ? layoutSlots.find(l => l.id === activeLayoutPreviewId) && layoutSlots.find(l => l.id === activeLayoutPreviewId)!.incrustations.some(i => i.sourceId === sourceId) : false;
    const isRemote = source && source.type === 'remote';
    const needsConfirm = isInFinal || isInPreview || sceneInFinal || sceneInPreview || isRemote;

    if (!needsConfirm) {
      onSlotPowerToggle(sourceId, false);
      return;
    }

    let msg = 'Voulez-vous vraiment éteindre cette source ?';
    if (isInFinal || sceneInFinal)     msg = 'Cette source est utilisée dans le flux final. Êtes-vous sûr de vouloir l\'éteindre ?';
    if (isInPreview || sceneInPreview) msg = 'Cette source est en preview. Êtes-vous sûr de vouloir l\'éteindre ?';
    if (isRemote)                      msg = 'Éteindre une source distante coupera la connexion WebRTC. Confirmer ?';

    setPendingPowerOff({ sourceId, message: msg });
  }, [selectedVideoSource, activeLayoutId, activeLayoutPreviewId, deckPreviewSourceId, layoutSlots, onSlotPowerToggle]);

  const cfgHlsStatus = ((): HlsProbeStatus | undefined => {
    if (configSlotIndex === null) return undefined;
    const row = videoRow[configSlotIndex];
    if (!row) return undefined;
    if (row.isHlsVideo) return hlsProbeStatusMap.get(`hls-video-${channelUUID}`);
    if (row.isHlsLive)  return hlsProbeStatusMap.get(`hls-live-${channelUUID}`);
    if (row.slot && row.slot.type === 'url') return hlsProbeStatusMap.get(row.slot.id);
    return undefined;
  })();

  const cfgOnRetryHls = ((): (() => void) | undefined => {
    if (!cfgHlsStatus || cfgHlsStatus !== 'failed' || configSlotIndex === null) return undefined;
    const row = videoRow[configSlotIndex];
    if (!row) return undefined;
    if (row.isHlsVideo) return function() { onRetryHls(`hls-video-${channelUUID}`, hlsUrl(true)); };
    if (row.isHlsLive)  return function() { onRetryHls(`hls-live-${channelUUID}`, hlsUrl(false)); };
    if (row.slot && row.slot.type === 'url' && row.slot.externalUrl) {
      const id = row.slot.id;
      const url = row.slot.externalUrl;
      return function() { onRetryHls(id, url); };
    }
    return undefined;
  })();

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-slate-700">
      <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
        <span className="text-lg">Deck</span>
        <button onClick={() => setPupitreConfigOpen(true)} className="ml-auto text-xs text-slate-400 hover:text-white flex items-center gap-1">
          <Settings className="w-3.5 h-3.5" /> Config pupitre
        </button>
        <button onClick={onOpenMediaboardConfig} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 ml-2"><Settings className="w-3.5 h-3.5" /> Config medias</button>
        <button onClick={onOpenDeckFullscreen} title="Ouvrir le deck dans un nouvel onglet" className="text-xs text-slate-400 hover:text-white flex items-center gap-1 ml-2"><ExternalLink className="w-3.5 h-3.5" /></button>
      </h2>

      {/*
        AJOUT : quand isRegiePowered est false, tout le contenu du deck devient
        semi-transparent et non-interactif (pointer-events:none).
        Une seule propriété CSS suffit — pas besoin de toucher chaque bouton.
      */}
      <div
        className="bg-slate-900 rounded-xl border border-slate-700 shadow-inner"
        style={{
          padding: 16,
          opacity: isRegiePowered ? 1 : 0.35,
          pointerEvents: isRegiePowered ? 'auto' : 'none',
          transition: 'opacity 0.2s',
        }}
      >
        <div className="space-y-3">

          {/* LIGNE 1 — Sources vidéo */}
          <div>
            <RowLabel icon={<Video className="w-3 h-3" />}>Sources vidéo</RowLabel>
            <div style={GRID_14_STRETCH}>
              {Array.from({ length: SLOT_COUNT }, (_, idx) => {
                const srcId = srcIdForSlot(idx);
                const isOff = poweredOffSlots.has(srcId);
                const row   = videoRow[idx];

                // CORRECTION : le bouton Power ne doit apparaître que si le slot
                // a une source affectée (source réelle, HLS video ou HLS live).
                // Un slot complètement vide n'a rien à éteindre.
                const hasSource =
                  !!(row && (row.slot || row.isHlsVideo || row.isHlsLive))
                  || !!deckSlotLayouts[idx];

                let onRetry: (() => void) | undefined;
                if (!isOff && row) {
                  if (row.isHlsVideo && hlsProbeStatusMap.get(`hls-video-${channelUUID}`) === 'failed')
                    onRetry = function() { onRetryHls(`hls-video-${channelUUID}`, hlsUrl(true)); };
                  else if (row.isHlsLive && hlsProbeStatusMap.get(`hls-live-${channelUUID}`) === 'failed')
                    onRetry = function() { onRetryHls(`hls-live-${channelUUID}`, hlsUrl(false)); };
                  else if (row.slot && row.slot.type === 'url' && row.slot.externalUrl
                           && hlsProbeStatusMap.get(row.slot.id) === 'failed') {
                    const id = row.slot.id;
                    const url = row.slot.externalUrl;
                    onRetry = function() { onRetryHls(id, url); };
                  }
                }

                return (
                  <VideoDeckButton key={idx}
                    label={slotLabel(idx)}
                    state={slotState(idx)}
                    isActive={
                      !isOff && (
                        deckSlotLayouts[idx]
                          ? activeLayoutId === deckSlotLayouts[idx]!.id
                          : !!(row && row.slot && row.slot.id === selectedVideoSource && !activeLayoutId)
                      )
                    }
                    disabled={slotDisabled(idx)}
                    keyBinding={keyMap.video[idx] ? formatKeyBinding(keyMap.video[idx]) : null}
                    title={slotTitle(idx)}
                    onClick={() => handleSlotClick(idx)}
                    sourceId={srcId}
                    audioLevel={sourceAudioLevels.get(srcId) || 0}
                    audioCfg={getSourceAudioConfig(srcId)}
                    onVolumeChange={function(v) { onSourceVolumeChange(srcId, v); }}
                    onMuteToggle={function() { onSourceMuteToggle(srcId); }}
                    onOpenConfig={function() { handleOpenSourceConfig(idx); }}
                    onRetry={onRetry}
                    isPoweredOff={isOff}
                    // CORRECTION : undefined si pas de source → powerBtn ne s'affiche pas
                    onTogglePower={hasSource
                      ? function() {
                          handlePowerOffRequest(
                            srcId, isOff,
                            row ? row.slot : null,
                            row || { slot: null, badge: '', isHlsVideo: false, isHlsLive: false },
                          );
                        }
                      : undefined}
                  />
                );
              })}
              <div /><div />
            </div>
          </div>

          {/* LIGNES 2-5 */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(12, 1fr) 12px 1fr', gap:6, alignItems:'end' }}>
            <div style={{ gridColumn:'1 / 14' }}><RowLabel icon={<Eye className="w-3 h-3" />}>Preview source</RowLabel></div>
            <div />
            {Array.from({ length: SLOT_COUNT }, (_, idx) => (
              <DeckButton key={idx}
                isActive={!poweredOffSlots.has(srcIdForSlot(idx)) && (deckSlotLayouts[idx] ? activeLayoutPreviewId === deckSlotLayouts[idx]!.id : deckPreviewSourceId === (resolvePreviewSource(idx) ? resolvePreviewSource(idx)!.id : null))}
                label={previewSlotLabel(idx)} state={previewSlotState(idx)} disabled={previewSlotDisabled(idx)}
                title={`Aperçu : ${previewSlotLabel(idx) || `Slot ${idx+1} — vide`}`}
                keyBinding={keyMap.preview && keyMap.preview[idx] ? formatKeyBinding(keyMap.preview[idx]) : null}
                onClick={() => handlePreviewClick(idx)} />
            ))}
            <div />

            {/* CUT */}
            <button onClick={handleCut} disabled={!cutEnabled}
              title={cutEnabled ? `CUT${cutKeyLabel ? ` [${cutKeyLabel}]` : ''}` : 'Sélectionnez une source en preview'}
              style={{ aspectRatio:'1', width:'100%', background:cutFlash?'linear-gradient(135deg,#dc2626,#ef4444)':!cutEnabled?'#1e293b':'linear-gradient(135deg,#7f1d1d,#991b1b)', border:`2px solid ${cutFlash?'#fca5a5':!cutEnabled?'#334155':'#ef4444'}`, borderRadius:8, cursor:!cutEnabled?'not-allowed':'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4, transition:'all 0.15s', boxShadow:cutFlash?'0 0 16px #ef444480, inset 0 2px 4px rgba(0,0,0,0.4)':'inset 0 2px 4px rgba(0,0,0,0.4)', opacity:!cutEnabled?0.4:1, padding:0, minWidth:0 }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:cutFlash?'#fff':!cutEnabled?'#334155':'#fca5a5', boxShadow:cutFlash?'0 0 8px #fff':'none' }} />
              <span style={{ fontSize:11, fontWeight:800, fontFamily:'monospace', color:'#fff', lineHeight:1 }}>CUT</span>
              {cutKeyLabel && <span style={{ fontSize:8, opacity:0.65, fontFamily:'monospace', color:'#fff', lineHeight:1 }}>[{cutKeyLabel}]</span>}
            </button>

            <div style={{ gridColumn:'1 / 14' }}><RowLabel icon={<Image className="w-3 h-3" />}>Images</RowLabel></div>
            <div style={{ gridRow:'span 6', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <TBarFader value={tbarValue} onChange={function(v) { if (onTbarChange) onTbarChange(v); }} disabled={!onTbarChange} />
            </div>
            {imageSlots.map((slot, idx) => (
              <DeckButton key={slot.id} label={slot.url ? slot.label : null} isActive={activeImageIds.has(slot.id)}
                state={stateOfRow('image', !!slot.url, activeImageIds.has(slot.id))} disabled={!slot.url}
                keyBinding={keyMap.image[idx] ? formatKeyBinding(keyMap.image[idx]) : null}
                title={!slot.url ? `Image ${idx+1}` : cleanLabel(slot.label)}
                onClick={() => onToggleImage(slot.id)} />
            ))}
            <div />

            <div style={{ gridColumn:'1 / 14' }}><RowLabel icon={<Music className="w-3 h-3" />}>Sons</RowLabel></div>
            {soundSlots.map((slot, idx) => (
              <DeckButton key={slot.id} label={slot.url ? slot.label : null} isActive={activeSoundIds.has(slot.id)}
                state={stateOfRow('sound', !!slot.url, activeSoundIds.has(slot.id))} disabled={!slot.url}
                keyBinding={keyMap.sound[idx] ? formatKeyBinding(keyMap.sound[idx]) : null}
                title={!slot.url ? `Son ${idx+1}` : cleanLabel(slot.label)}
                onClick={() => onTriggerSound(slot)} />
            ))}
            <div />

            <div style={{ gridColumn:'1 / 14' }}><RowLabel icon={<Film className="w-3 h-3" />}>Vidéo</RowLabel></div>
            {jingleSlots.map((slot, idx) => (
              <DeckButton key={slot.id} label={slot.url ? slot.label : null} isActive={activeJingleId === slot.id}
                state={stateOfRow('jingle', !!slot.url, activeJingleId === slot.id)} disabled={!slot.url}
                keyBinding={keyMap.jingle && keyMap.jingle[idx] ? formatKeyBinding(keyMap.jingle[idx]) : null}
                title={!slot.url ? `Jingle ${idx+1}` : cleanLabel(slot.label)}
                onClick={() => onTriggerJingle(slot)}
                extra={slot.url && slot.useCanvas ? <span style={{ fontSize:7, opacity:0.5, color:'#fff' }}>[canvas]</span> : null} />
            ))}
            <div />
          </div>

          {/* LIGNE 6 — Scènes */}
          <div>
            <RowLabel icon={<Clapperboard className="w-3 h-3" />}>Scènes</RowLabel>
            <div style={GRID_14}>
              {layoutSlots.map((slot, idx) => {
                const hasContent = slot.background.type !== 'none' || slot.incrustations.length > 0;
                return (
                  <DeckButton key={slot.id} label={hasContent ? slot.label : null} isActive={activeLayoutId === slot.id}
                    state={stateOfRow('layout', hasContent, activeLayoutId === slot.id)} disabled={!hasContent}
                    keyBinding={keyMap.layout && keyMap.layout[idx] ? formatKeyBinding(keyMap.layout[idx]) : null}
                    title={!hasContent ? `Scène ${idx+1}` : cleanLabel(slot.label)}
                    onClick={() => onToggleLayout(slot)} />
                );
              })}
              <div /><div />
            </div>
          </div>

          {/* PiP (masqué) */}
          <div style={{ display:'none' }}>
            <RowLabel>PiP vidéo</RowLabel>
            <div className="grid grid-cols-12 gap-1.5">
              {videoOverlaySlots.map((slot) => {
                const empty = slot.sourceType === 'remote' && !slot.remoteDeviceId;
                const s = stateOfRow('pip', !empty, activeVideoOverlayIds.has(slot.id));
                return (
                  <button key={slot.id} onClick={() => onStartVideoOverlay(slot)} disabled={empty}
                    style={{ aspectRatio:'2/1', background:s.bg, border:`2px solid ${s.border}`, borderRadius:8, cursor:empty?'not-allowed':'pointer', padding:'6px 10px', display:'flex', alignItems:'center', gap:8, transition:'all 0.15s', boxShadow:`${s.shadow}, inset 0 2px 4px rgba(0,0,0,0.4)` }}>
                    <div style={{ width:7, height:7, borderRadius:'50%', background:s.led }} />
                    <span style={{ fontSize:11, fontWeight:600, color:'#fff', opacity:empty?0.3:1 }}>{cleanLabel(slot.label)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Légende */}
          <div className="flex items-center gap-5 mt-2 px-1">
            {([
              [EMPTY.led,                  'Inutilisé / Éteint'],
              [PROBING_STATE.led,          'En cours de vérification'],
              [PALETTES.video.avail.led,   'Disponible'],
              [PALETTES.video.active.led,  'En final'],
              [PALETTES.preview.avail.led, 'Prévisualisable'],
              [PALETTES.preview.active.led,'En preview'],
              [PALETTES.layout.avail.led,  'Scène (rangée 6)'],
            ] as [string, string][]).map(([color, label]) => (
              <div key={label} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background:color, boxShadow:color!==EMPTY.led?`0 0 4px ${color}`:'none' }} />
                <span className="text-xs text-slate-400">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modale Config pupitre */}
      {pupitreConfigOpen && (
        <PupitreConfigModalWrapper
          onClose={function() { setPupitreConfigOpen(false); }}
          videoRow={videoRow}
          deckSlots={deckSlots}
          deckSlotLayouts={deckSlotLayouts}
          availableSources={availableSources ? availableSources : []}
          sourceAliases={sourceAliases}
          onAliasesChange={onAliasesChange}
          onSlotSourceChange={onSlotSourceChange}
          onSlotLayoutChange={onSlotLayoutChange}
          jingleSlots={jingleSlots}
          layoutSlots={layoutSlots}
          imageSlots={imageSlots}
          soundSlots={soundSlots}
          videoOverlaySlots={videoOverlaySlots}
          activeJingleId={activeJingleId}
          activeLayoutId={activeLayoutId}
          onTriggerJingle={onTriggerJingle}
          onToggleLayout={onToggleLayout}
          keyMap={keyMap}
          onKeyMapChange={onKeyMapChange}
          slotLabelFn={slotLabel}
        />
      )}

      {/* Modale config source individuelle */}
      {configSlotIndex !== null && (function() {
        const row = videoRow[configSlotIndex];
        const cfgLayout = deckSlotLayouts[configSlotIndex] ? deckSlotLayouts[configSlotIndex] : null;
        const cfgSource = row ? (row.slot ? row.slot : null) : null;
        const cfgSourceResolved = cfgSource
          ? cfgSource
          : (row && row.isHlsVideo ? buildHls(true) : row && row.isHlsLive ? buildHls(false) : null);

        const deckSrcIds2: Set<string> = new Set();
        for (let vi = 0; vi < videoRow.length; vi++) {
          if (videoRow[vi].slot) deckSrcIds2.add(videoRow[vi].slot!.id);
        }
        const orderedSources: VideoSource[] = [];
        for (let vi = 0; vi < videoRow.length; vi++) {
          if (videoRow[vi].slot) orderedSources.push(videoRow[vi].slot!);
        }
        const allAvail = availableSources ? availableSources : [];
        for (let i = 0; i < allAvail.length; i++) {
          if (!deckSrcIds2.has(allAvail[i].id)) orderedSources.push(allAvail[i]);
        }

        const videoRowSlots: Array<VideoSource | null> = [];
        for (let vi = 0; vi < videoRow.length; vi++) {
          videoRowSlots.push(videoRow[vi].slot ? videoRow[vi].slot : null);
        }

        const srcId = cfgLayout ? cfgLayout.id : (cfgSource ? cfgSource.id : '');
        return (
          <SourceConfigModal
            slotIndex={configSlotIndex} source={cfgSourceResolved} currentLayout={cfgLayout}
            availableSources={orderedSources}
            deckSlots={videoRowSlots}
            availableAudioDevices={availableAudioDevices ? availableAudioDevices : []}
            aliases={sourceAliases}
            onAliasChange={function(sid, alias) { if (onAliasChange) onAliasChange(sid, alias); }}
            onSlotSourceChange={onSlotSourceChange} onSlotLayoutChange={onSlotLayoutChange}
            selectedAudioDeviceId={selectedAudioDeviceIds ? (selectedAudioDeviceIds.get(srcId) ? selectedAudioDeviceIds.get(srcId)! : '') : ''}
            onAudioDeviceChange={function(sid, devId) { if (onAudioDeviceChange) onAudioDeviceChange(sid, devId); }}
            meterSourceId={srcId}
            stereoVolume={stereoVolumes && stereoVolumes.get(srcId) ? stereoVolumes.get(srcId)! : DEFAULT_STEREO_VOLUME}
            onStereoVolumeChange={function(sid, sv) { if (onStereoVolumeChange) onStereoVolumeChange(sid, sv); }}
            audioCfg={getSourceAudioConfig(srcId)}
            onMuteToggle={function(sid) { onSourceMuteToggle(sid); }}
            audioEffects={audioEffectsMap && audioEffectsMap.get(srcId) ? audioEffectsMap.get(srcId)! : DEFAULT_AUDIO_EFFECTS}
            onToggleAudioEffect={function(sid, fx) { if (onToggleAudioEffect) onToggleAudioEffect(sid, fx); }}
            isLiveSlot={!!cfgSource && selectedVideoSource === cfgSource.id}
            onSwitchLive={onDeckSwitchSource ? function(src) { onDeckSwitchSource(src); } : undefined}
            onClose={handleCloseSourceConfig}
            jingleSlots={jingleSlots} layoutSlots={layoutSlots}
            activeJingleId={activeJingleId} activeLayoutId={activeLayoutId}
            onTriggerJingle={onTriggerJingle} onToggleLayout={onToggleLayout}
            hlsProbeStatus={cfgHlsStatus} onRetryHls={cfgOnRetryHls}
            keyMap={keyMap} onKeyMapChange={onKeyMapChange}
          />
        );
      })()}

      {/* Confirmation Power Off */}
      {pendingPowerOff && (
        <Window
          message={<>{pendingPowerOff.message}</>}
          messageType="question"
          messageImportance="important"
          onClickOnValidateButton={function() {
            onSlotPowerToggle(pendingPowerOff.sourceId, false);
            setPendingPowerOff(null);
          }}
          onClickOnCancelButton={function() { setPendingPowerOff(null); }}
        />
      )}
    </div>
  );
};

export default ProductionControlRoom_Deck;